import React, { useEffect, useMemo, useState } from "react";
import Button from "../../shared/Button";
import {
  useYearLevelBySubject,
  useRecruitmentStatus,
  useRecruitment,
} from "../../../hooks/useSubjects";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import AssignTeacherRow from "./AssignTeacherRow";
import { Spinner } from "reactstrap";

const AssignTeacher = ({ subject }) => {
  const {
    yearLevelBySubject,
    insertSubjectClassroomTeacher,
    deleteSubjectClassroomTeacher,
    loading,
  } = useYearLevelBySubject(subject.id);
  const { recruitmentStatus } = useRecruitmentStatus();
  const { staffList, getStaffList } = useRecruitment();
  const [isOpen, setIsOpen] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const staffListOptions = useMemo(() => {
    if (staffList?.length === 0) return;
    return staffList?.map((item) => ({
      label: item?.people?.name,
      value: item.id,
    }));
  }, [staffList, recruitmentStatus]);

  //extract yearlevel name and id for tabs
  const yearLevels = useMemo(() => {
    if (!yearLevelBySubject) return [];

    return yearLevelBySubject.reduce((acc, item) => {
      if (!acc.find((i) => i.id === item.id)) {
        acc.push({ id: item.id, name: item.name });
      }
      return acc;
    }, []);
  }, [yearLevelBySubject]);

  //filter class and division for mapping
  const selectedYearLevelBySubject = useMemo(() => {
    return yearLevelBySubject?.filter((item) => item.id === activeTab);
  }, [activeTab, yearLevels]);

  //fetch staffs
  useEffect(() => {
    if (recruitmentStatus?.length > 0) {
      const recruitmentStatusId = recruitmentStatus.find(
        (item) => item?.code === "rt-cur",
      )?.id;
      getStaffList(recruitmentStatusId);
    }
  }, [recruitmentStatus]);

  // set first item as active
  useEffect(() => {
    if (yearLevels?.length > 0) {
      setActiveTab(yearLevels[0]?.id);
    }
  }, [yearLevels]);

  return (
    <>
      {loading.read ? (
        <div className="new-spinner-container">
          <div className="new-spinner">
            <div className="loader">
              <Spinner color="primary" />
            </div>
            <div style={{ fontSize: "16px", marginTop: "15px" }}>
              Please wait !! Processing.....
            </div>
          </div>
        </div>
      ) : yearLevelBySubject?.length > 0 ? (
        <>
          <div className="arr-tabs">
            {yearLevels?.map((item) => (
              <Button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                variant={activeTab === item.id ? "primary" : "outline"}
              >
                Class {item.name}
              </Button>
            ))}
          </div>

          {selectedYearLevelBySubject?.map((item) => (
            <div
              style={{ marginTop: "16px" }}
              key={`${item.id}-${item?.educationLevelDivisionName}`}
              role="arr-accordion"
              className="arr-accordion"
            >
              <div
                role="accordion-btn"
                className="flex"
                style={{
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (
                    isOpen === `${item.id}-${item?.educationLevelDivisionName}`
                  ) {
                    setIsOpen(null);
                    return;
                  }
                  setIsOpen(`${item.id}-${item?.educationLevelDivisionName}`);
                }}
              >
                <p style={{ margin: 0 }}>
                  Class {item?.name} - {item?.educationLevelDivisionName} Term
                </p>
                <FontAwesomeIcon fontSize={14} icon={faChevronDown} />
              </div>

              {isOpen === `${item.id}-${item?.educationLevelDivisionName}` && (
                <div className="arr-table-container noShadow">
                  <table aria-label="arr-table" className="arr-table">
                    <thead>
                      <tr>
                        <th>Section</th>
                        <th>Assigned Teachers</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {item?.classrooms?.length > 0 ? (
                        item?.classrooms?.map((classroom) => (
                          <AssignTeacherRow
                            key={classroom.id}
                            subjectId={subject.id}
                            classroom={classroom}
                            staffListOptions={staffListOptions}
                            insertSubjectClassroomTeacher={
                              insertSubjectClassroomTeacher
                            }
                            deleteSubjectClassroomTeacher={
                              deleteSubjectClassroomTeacher
                            }
                            loading={loading}
                          />
                        ))
                      ) : (
                        <tr>
                          <td style={{ textAlign: "center" }} colSpan={3}>
                            No Section found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </>
      ) : (
        <div className="empty-data">
          Oops, no data found. Please configure the settings first.
        </div>
      )}
    </>
  );
};

export default AssignTeacher;
